import { createWorld, addComponent, addEntity } from 'bitecs';
import { Position, Tile } from '../world.js';
import { TILE_SIZE, MAP_WIDTH, MAP_HEIGHT, NOISE_SCALE, NOISE_MULTIPLIER, ISLAND_BASE_RADIUS, BIOME_SCALE } from '../config.js';
import { tileManager, TILE_TYPES, BIOME_TYPES } from './tileManager.js';
import { createNoise2D } from 'simplex-noise';

// Create noise generators with different random seeds for each game start
let heightNoise = null;
let biomeNoise = null;
let detailNoise = null;
let warpNoise = null;
let moistureNoise = null;

// Define biome thresholds at module scope
const WATER_LEVEL = 0.15;
const BEACH_LEVEL = 0.25;
const PLAINS_LEVEL = 0.65;
const FOREST_LEVEL = 0.85;

// Define warping constants
const WARP_SCALE = 0.02;    // How large the warping features are
const WARP_STRENGTH = 80.0; // How much the coordinates are offset (in map units)

// Define biome determination constants
const MOISTURE_SCALE = 0.04; // Scale for moisture noise (adjust for feature size)
const MOUNTAIN_LEVEL = 0.70; // Height threshold for mountains (lower than before)
const FOREST_MOISTURE_THRESHOLD = 0.55; // Moisture level above which forests appear (instead of plains)

function initializeNoiseGenerators() {
    // Create new noise generators with random seeds
    heightNoise = createNoise2D(() => Math.random());
    biomeNoise = createNoise2D(() => Math.random());
    detailNoise = createNoise2D(() => Math.random());
    warpNoise = createNoise2D(() => Math.random());
    moistureNoise = createNoise2D(() => Math.random());
}

function generateHeight(x, y, centerX, centerY) {
    // --- Warping --- 
    // Get warp noise values
    const warpXOffset = warpNoise(x * WARP_SCALE, y * WARP_SCALE) * WARP_STRENGTH;
    const warpYOffset = warpNoise(y * WARP_SCALE, x * WARP_SCALE) * WARP_STRENGTH; // Use different coords for Y warp

    // Apply warp to coordinates used for distance calculation
    const warpedX = x + warpXOffset;
    const warpedY = y + warpYOffset;
    // --- End Warping ---

    // Calculate normalized distance from center using *warped* coordinates
    const effectiveRadius = Math.max(centerX, centerY);
    const dx = warpedX - centerX;
    const dy = warpedY - centerY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const normalizedDistance = Math.min(1.0, dist / effectiveRadius); 

    // Generate multiple layers of height/detail noise (using original x, y)
    const baseNoise = heightNoise(x * NOISE_SCALE, y * NOISE_SCALE);
    const detailNoiseValue = detailNoise(x * NOISE_SCALE * 2, y * NOISE_SCALE * 2) * 0.5; 
    
    // Combine height/detail noise layers - normalize roughly to [0, 1]
    let combinedNoise = (baseNoise + detailNoiseValue) / 1.5; 
    combinedNoise = (combinedNoise + 1.0) / 2.0; // Shift to ~[0, 1]
    
    // Apply Radial Falloff Factor based on *warped* distance
    const falloffPower = 2.5; 
    const falloff = Math.max(0.0, 1.0 - Math.pow(normalizedDistance, falloffPower));
    
    // Apply falloff *to* the combined height/detail noise
    let finalHeight = combinedNoise * falloff;
    
    // Scale the result by the overall multiplier
    finalHeight *= NOISE_MULTIPLIER; 

    // Clamp the result
    finalHeight = Math.max(0.0, Math.min(1.0, finalHeight));

    return finalHeight;

    /* // --- Previous method (No warp) ---
    // Calculate normalized distance from center (0 at center, 1 at edge/corner)
    // Use distance to corner for normalization
    const maxDist = Math.sqrt(centerX * centerX + centerY * centerY);
    const dx = x - centerX;
    const dy = y - centerY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    // Normalize against effective radius (e.g., half map width) for a generally central island
    const effectiveRadius = Math.max(centerX, centerY); 
    const normalizedDistance = Math.min(1.0, dist / effectiveRadius); 

    // Generate multiple layers of noise
    const baseNoise = heightNoise(x * NOISE_SCALE, y * NOISE_SCALE);
    const detailNoiseValue = detailNoise(x * NOISE_SCALE * 2, y * NOISE_SCALE * 2) * 0.5; 
    
    // Combine noise layers - normalize noise output roughly to [0, 1]
    let combinedNoise = (baseNoise + detailNoiseValue) / 1.5; // Adjust divisor if needed
    combinedNoise = (combinedNoise + 1.0) / 2.0; // Shift to ~[0, 1]
    
    // Apply Radial Falloff Factor
    const falloffPower = 2.5; // Steeper drop-off towards edges
    const falloff = Math.max(0.0, 1.0 - Math.pow(normalizedDistance, falloffPower));
    
    // Apply falloff *to* the noise height
    let finalHeight = combinedNoise * falloff;
    
    // Scale the result by the overall multiplier
    finalHeight *= NOISE_MULTIPLIER; 

    // Clamp the result
    finalHeight = Math.max(0.0, Math.min(1.0, finalHeight));

    return finalHeight;
    */

    /* // --- Old method --- 
    // Calculate distance from center
    const distance = Math.sqrt(
        Math.pow(x - centerX, 2) + 
        Math.pow(y - centerY, 2)
    );
    
    // Generate multiple layers of noise
    const baseNoise = heightNoise(x * NOISE_SCALE, y * NOISE_SCALE) * NOISE_MULTIPLIER;
    const detailNoiseValue = detailNoise(x * NOISE_SCALE * 2, y * NOISE_SCALE * 2) * (NOISE_MULTIPLIER * 0.5);
    
    // Combine noise layers
    const combinedNoise = baseNoise + detailNoiseValue;
    
    // Create island shape with noise
    const radius = ISLAND_BASE_RADIUS + combinedNoise;
    
    if (distance < radius) {
        // Smooth falloff from center
        const height = 1 - (distance / radius);
        // Add noise to height
        const heightNoiseValue = heightNoise(x * NOISE_SCALE * 0.5, y * NOISE_SCALE * 0.5) * (0.3 * BIOME_SCALE);
        return Math.max(0, Math.min(1, height + heightNoiseValue));
    }
    return 0;
    */
}

function determineBiome(height, x, y) {
    // Keep original biome thresholds for water/beach based on height
    if (height <= WATER_LEVEL) return BIOME_TYPES.WATER;
    if (height <= BEACH_LEVEL) return BIOME_TYPES.BEACH;

    // Generate moisture value using noise, normalized roughly to [0, 1]
    let moisture = (moistureNoise(x * MOISTURE_SCALE, y * MOISTURE_SCALE) + 1.0) / 2.0;
    
    // Optional: Add slight bias based on height (lower areas slightly more moist) - subtle effect
    // moisture += (0.3 - height) * 0.1; // Add small moisture boost for lower elevations 
    // moisture = Math.max(0.0, Math.min(1.0, moisture)); // Clamp moisture

    // Determine land biome based on height and moisture
    if (height >= MOUNTAIN_LEVEL) { 
        return BIOME_TYPES.MOUNTAINS; // High areas are mountains regardless of moisture
    }
    
    if (moisture >= FOREST_MOISTURE_THRESHOLD) {
        return BIOME_TYPES.FOREST; // Sufficient moisture leads to forest
    } else {
        return BIOME_TYPES.PLAINS; // Drier areas become plains
    }

    /* // --- Old height-based method ---
    // Generate biome noise
    const biomeNoiseValue = biomeNoise(x * NOISE_SCALE * 0.3, y * NOISE_SCALE * 0.3) * (0.15 * BIOME_SCALE);
    // Ensure biome noise doesn't make land appear where height is 0 (deep water)
    const adjustedHeight = height > 0 ? height + biomeNoiseValue : 0; 

    // *** Use module-scoped constants ***

    if (adjustedHeight <= WATER_LEVEL) return BIOME_TYPES.WATER;
    if (adjustedHeight <= BEACH_LEVEL) return BIOME_TYPES.BEACH;
    if (adjustedHeight <= PLAINS_LEVEL) return BIOME_TYPES.PLAINS;
    if (adjustedHeight <= FOREST_LEVEL) return BIOME_TYPES.FOREST;
    return BIOME_TYPES.MOUNTAINS;
    */
}

// Generate a realistic island map
export function mapGenerationSystem(world) {
    // Initialize noise generators with new random seeds
    initializeNoiseGenerators();
    
    // Clear existing tiles
    world.entities = world.entities.filter(eid => !Tile[eid]);
    
    const centerX = MAP_WIDTH / 2;
    const centerY = MAP_HEIGHT / 2;
    
    // Generate new map
    for (let x = 0; x < MAP_WIDTH; x++) {
        for (let y = 0; y < MAP_HEIGHT; y++) {
            // Generate height with noise and falloff
            const height = generateHeight(x, y, centerX, centerY);
            
            // Determine tile type based on new water level (now WATER_LEVEL is accessible)
            const type = height > WATER_LEVEL ? TILE_TYPES.LAND : TILE_TYPES.WATER;
            
            // Determine biome with noise
            const biome = determineBiome(height, x, y);
            
            // Create tile entity
            const tile = addEntity(world);
            addComponent(world, Tile, tile);
            addComponent(world, Position, tile);
            
            // Set tile properties
            Tile.type[tile] = type;
            Tile.x[tile] = x;
            Tile.y[tile] = y;
            Tile.height[tile] = height;
            Tile.biome[tile] = biome;
            
            // Set position
            Position.x[tile] = x * TILE_SIZE;
            Position.y[tile] = y * TILE_SIZE;
            
            // Add to world entities
            world.entities.push(tile);
            
            // Set tile in tile manager
            tileManager.setTile(x, y, type, height, biome);
        }
    }
    
    return world;
} 